import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { displayBonusLabel, getZoneBonusTrends, type BonusType, type ZoneTrendRow } from '../api/bonuses';
import { ResourceHunterTabs } from '../components/resourceHunter/ResourceHunterTabs';
import { ResourceBonusGridControls } from '../components/resourceHunter/ResourceBonusGridControls';
import { ZoneMetadataLine } from '../components/resourceHunter/ZoneMetadataLine';
import {
  DEFAULT_RESOURCE_BONUS_FILTERS,
  DEFAULT_RESOURCE_BONUS_SORT,
  filterAndSortBonusRows,
  getResourceBonusFilterOptions,
  hasActiveResourceBonusFilters,
  type ResourceBonusGridFilters,
  type ResourceBonusGridSort
} from '../components/resourceHunter/resourceHunterUtils';
import { getActiveServerSlug } from '../utils/siteContext';

function heatmapBucket(count: number, max: number) {
  if (count <= 0 || max <= 0) {
    return 0;
  }

  const ratio = Math.log1p(count) / Math.log1p(max);

  if (ratio >= 0.86) {
    return 6;
  }
  if (ratio >= 0.7) {
    return 5;
  }
  if (ratio >= 0.54) {
    return 4;
  }
  if (ratio >= 0.38) {
    return 3;
  }
  if (ratio >= 0.2) {
    return 2;
  }

  return 1;
}

function heatmapCellStyle(count: number, max: number): CSSProperties {
  const bucket = heatmapBucket(count, max);

  const colors = [
    'rgba(255, 255, 255, 0.05)',
    '#2454a6',
    '#1687a7',
    '#23a66f',
    '#d1a233',
    '#d86b27',
    '#e6462f'
  ];

  const glows = [
    'none',
    '0 0 10px rgba(36, 84, 166, 0.14)',
    '0 0 10px rgba(22, 135, 167, 0.16)',
    '0 0 10px rgba(35, 166, 111, 0.18)',
    '0 0 10px rgba(209, 162, 51, 0.2)',
    '0 0 10px rgba(216, 107, 39, 0.22)',
    '0 0 12px rgba(230, 70, 47, 0.28)'
  ];

  return {
    backgroundColor: colors[bucket],
    boxShadow: glows[bucket]
  };
}

function isTrendBonusType(type: BonusType) {
  return type.key !== 'none';
}

function visibleBonusTotal(zone: ZoneTrendRow, bonusTypes: BonusType[]) {
  return bonusTypes.reduce((sum, type) => sum + (zone.counts[type.key] ?? 0), 0);
}

function shortBonusLabel(type: BonusType) {
  if (type.key === 'experience') {
    return 'EXP';
  }
  if (type.key === 'tradeskill') {
    return 'Skill';
  }
  if (type.key === 'none') {
    return 'None';
  }

  return displayBonusLabel(type);
}


const SORT_BY_VALUES: ResourceBonusGridSort['sortBy'][] = ['name', 'level', 'expansion', 'totalBonuses'];
const SORT_DIRECTION_VALUES: ResourceBonusGridSort['direction'][] = ['asc', 'desc'];

function parseFilters(searchParams: URLSearchParams): ResourceBonusGridFilters {
  return {
    expansions: searchParams.getAll('expansion').filter(Boolean),
    zoneType: searchParams.get('zoneType') ?? '',
    minLevel: searchParams.get('minLevel') ?? '',
    maxLevel: searchParams.get('maxLevel') ?? ''
  };
}

function parseSort(searchParams: URLSearchParams): ResourceBonusGridSort {
  const sortByParam = searchParams.get('sortBy');
  const directionParam = searchParams.get('order');

  return {
    sortBy: SORT_BY_VALUES.includes(sortByParam as ResourceBonusGridSort['sortBy'])
      ? (sortByParam as ResourceBonusGridSort['sortBy'])
      : DEFAULT_RESOURCE_BONUS_SORT.sortBy,
    direction: SORT_DIRECTION_VALUES.includes(directionParam as ResourceBonusGridSort['direction'])
      ? (directionParam as ResourceBonusGridSort['direction'])
      : DEFAULT_RESOURCE_BONUS_SORT.direction
  };
}

function setOrDeleteParam(params: URLSearchParams, key: string, value: string, defaultValue = '') {
  const normalized = value.trim();

  if (!normalized || normalized === defaultValue) {
    params.delete(key);
    return;
  }

  params.set(key, normalized);
}


function areFiltersEqual(left: ResourceBonusGridFilters, right: ResourceBonusGridFilters) {
  return left.zoneType === right.zoneType
    && left.minLevel === right.minLevel
    && left.maxLevel === right.maxLevel
    && left.expansions.length === right.expansions.length
    && left.expansions.every((value, index) => value === right.expansions[index]);
}

function areSortsEqual(left: ResourceBonusGridSort, right: ResourceBonusGridSort) {
  return left.sortBy === right.sortBy && left.direction === right.direction;
}

function ZoneTrendMatrix({ bonusTypes, zones }: { bonusTypes: BonusType[]; zones: ZoneTrendRow[] }) {
  const visibleBonusTypes = bonusTypes.filter(isTrendBonusType);
  const maxCount = Math.max(
    ...zones.flatMap((zone) => visibleBonusTypes.map((type) => zone.counts[type.key] ?? 0)),
    0
  );

  const gridStyle = {
    '--zone-trend-column-count': visibleBonusTypes.length || 1
  } as CSSProperties;

  return (
    <div className="zone-trends-matrix" aria-label="Zone bonus trend heatmap" style={gridStyle}>
      <div className="zone-trends-matrix__header" aria-hidden="true">
        <div />
        <div className="zone-trends-matrix__heatmap-header">
          {visibleBonusTypes.map((type) => (
            <span key={type.id} title={displayBonusLabel(type)}>{shortBonusLabel(type)}</span>
          ))}
        </div>
      </div>

      <div className="zone-trends-matrix__rows">
        {zones.map((zone) => (
          <div className="zone-trends-matrix__row" key={zone.id}>
            <article className="bonus-zone-card bonus-zone-card--trend">
              <div>
                <Link className="fw-semibold" to={`/resource-hunter/zones/${zone.slug}`}>
                  {zone.name}
                </Link>
                <div className="small text-body-secondary mt-1">
                  <ZoneMetadataLine expansion={zone.expansion} minLevel={zone.minLevel} maxLevel={zone.maxLevel} zoneType={zone.zoneType} fallbackText="Zone metadata unavailable" />
                </div>
              </div>
              <span className="bonus-zone-card__total" title="Total tracked bonuses excluding None">{visibleBonusTotal(zone, visibleBonusTypes)}</span>
            </article>

            <div className="zone-trends-matrix__heatmap-row">
              {visibleBonusTypes.map((type) => {
                const count = zone.counts[type.key] ?? 0;

                return (
                  <span
                    aria-label={`${zone.name}: ${displayBonusLabel(type)} appeared ${count} ${count === 1 ? 'time' : 'times'}`}
                    className={`zone-trends-heatmap__cell heat-${heatmapBucket(count, maxCount)}`}
                    key={type.id}
                    style={heatmapCellStyle(count, maxCount)}
                    title={`${zone.name}: ${displayBonusLabel(type)} appeared ${count} ${count === 1 ? 'time' : 'times'}`}
                  >
                    <span className="visually-hidden">{count}</span>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



const TrendsFiltersSection = memo(function TrendsFiltersSection({
  filters,
  sort,
  expansionOptions,
  zoneTypeOptions,
  visibleCount,
  totalCount,
  hasActiveFilters,
  onFiltersChange,
  onSortChange,
  onResetFilters,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange
}: {
  filters: ResourceBonusGridFilters;
  sort: ResourceBonusGridSort;
  expansionOptions: string[];
  zoneTypeOptions: string[];
  visibleCount: number;
  totalCount: number;
  hasActiveFilters: boolean;
  onFiltersChange: (filters: ResourceBonusGridFilters) => void;
  onSortChange: (sort: ResourceBonusGridSort) => void;
  onResetFilters: () => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}) {
  return (
    <>
      <ResourceBonusGridControls
        filters={filters}
        sort={sort}
        expansionOptions={expansionOptions}
        zoneTypeOptions={zoneTypeOptions}
        visibleCount={visibleCount}
        totalCount={totalCount}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={onResetFilters}
        sortOptions={[
          { value: 'name', label: 'Name' },
          { value: 'level', label: 'Level' },
          { value: 'expansion', label: 'Expansion' },
          { value: 'totalBonuses', label: 'Total Bonuses' }
        ]}
      />

      <div className="d-flex gap-2 mb-3">
        <label className="resource-grid-control">
          <span>Start date</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(event) => onStartDateChange(event.target.value)}
          />
        </label>
        <label className="resource-grid-control">
          <span>End date</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(event) => onEndDateChange(event.target.value)}
          />
        </label>
      </div>
    </>
  );
});
export function BonusZonesPage() {
  const serverSlug = getActiveServerSlug();
  const serverDisplayName = serverSlug.charAt(0).toUpperCase() + serverSlug.slice(1);

  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const sort = useMemo(() => parseSort(searchParams), [searchParams]);

  function updateFilters(nextFilters: ResourceBonusGridFilters) {
    const next = new URLSearchParams(searchParams);
    next.delete('expansion');
    for (const expansion of nextFilters.expansions) {
      const normalized = expansion.trim();
      if (normalized) {
        next.append('expansion', normalized);
      }
    }
    setOrDeleteParam(next, 'zoneType', nextFilters.zoneType);
    setOrDeleteParam(next, 'minLevel', nextFilters.minLevel);
    setOrDeleteParam(next, 'maxLevel', nextFilters.maxLevel);
    setSearchParams(next, { replace: true });
  }

  function updateSort(nextSort: ResourceBonusGridSort) {
    const next = new URLSearchParams(searchParams);
    setOrDeleteParam(next, 'sortBy', nextSort.sortBy, DEFAULT_RESOURCE_BONUS_SORT.sortBy);
    setOrDeleteParam(next, 'order', nextSort.direction, DEFAULT_RESOURCE_BONUS_SORT.direction);
    setSearchParams(next, { replace: true });
  }

  function resetFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete('expansion');
    next.delete('zoneType');
    next.delete('minLevel');
    next.delete('maxLevel');
    next.delete('startDate');
    next.delete('endDate');
    setSearchParams(next, { replace: true });
  }

  const startDateFilter = searchParams.get('startDate') ?? '';
  const endDateFilter = searchParams.get('endDate') ?? '';

  const trendsQuery = useQuery({
    queryKey: ['bonus-zone-trends', serverSlug, startDateFilter, endDateFilter],
    queryFn: () => getZoneBonusTrends(serverSlug, {
      startDate: startDateFilter,
      endDate: endDateFilter
    })
  });

  const baseTrendsQuery = useQuery({
    queryKey: ['bonus-zone-trends', serverSlug, 'all-options'],
    queryFn: () => getZoneBonusTrends(serverSlug)
  });

  const bonusTypes = trendsQuery.data?.bonusTypes ?? [];
  const allZones = trendsQuery.data?.zones ?? [];

  const expansionOptions = useMemo(() => getResourceBonusFilterOptions(baseTrendsQuery.data?.zones ?? []).expansions, [baseTrendsQuery.data?.zones]);
  const zoneTypeOptions = useMemo(() => getResourceBonusFilterOptions(allZones).zoneTypes, [allZones]);
  const zones = useMemo(() => {
    const baseSort = sort.sortBy === 'totalBonuses' ? { ...sort, sortBy: 'name' as const } : sort;
    const filteredRows = filterAndSortBonusRows(allZones, filters, baseSort);

    if (sort.sortBy !== 'totalBonuses') {
      return filteredRows;
    }

    const visibleBonusTypes = bonusTypes.filter(isTrendBonusType);
    const directionMultiplier = sort.direction === 'desc' ? -1 : 1;

    return [...filteredRows].sort((a, b) => {
      const comparison = visibleBonusTotal(a, visibleBonusTypes) - visibleBonusTotal(b, visibleBonusTypes);

      if (comparison === 0) {
        return a.name.localeCompare(b.name);
      }

      return comparison * directionMultiplier;
    });
  }, [allZones, bonusTypes, filters, sort]);
  const hasActiveFilters = hasActiveResourceBonusFilters(filters);
  const hasActiveDateFilters = Boolean(startDateFilter.trim() || endDateFilter.trim());
  const showLoadingState = trendsQuery.isPending && !trendsQuery.data;

  return (
    <main className="main-content">
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-3">
            Resource Hunter Bonuses
            <span className="page-hero__server d-block">{serverDisplayName}</span>
          </h1>
          <p className="fs-3 mb-0">Zone Trends</p>
        </div>
      </section>

      <ResourceHunterTabs activeTab="trends" />

      <div className="content-wrapper py-4">
        <section className="report-panel zone-trends-panel">

          {!showLoadingState && !trendsQuery.isError ? (
            <ResourceBonusGridControls
              filters={filters}
              sort={sort}
              expansionOptions={expansionOptions}
              zoneTypeOptions={zoneTypeOptions}
              visibleCount={zones.length}
              totalCount={allZones.length}
              onFiltersChange={updateFilters}
              onSortChange={updateSort}
              hasActiveFilters={hasActiveFilters || hasActiveDateFilters}
              onResetFilters={resetFilters}
              sortOptions={[
                { value: 'name', label: 'Name' },
                { value: 'level', label: 'Level' },
                { value: 'expansion', label: 'Expansion' },
                { value: 'totalBonuses', label: 'Total Bonuses' }
              ]}
            />
          ) : null}

          {!showLoadingState && !trendsQuery.isError ? (
            <div className="d-flex gap-2 mb-3">
              <label className="resource-grid-control">
                <span>Start date</span>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={searchParams.get('startDate') ?? ''}
                  onChange={(event) => {
                    const next = new URLSearchParams(searchParams);
                    setOrDeleteParam(next, 'startDate', event.target.value);
                    setSearchParams(next, { replace: true });
                  }}
                />
              </label>
              <label className="resource-grid-control">
                <span>End date</span>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={searchParams.get('endDate') ?? ''}
                  onChange={(event) => {
                    const next = new URLSearchParams(searchParams);
                    setOrDeleteParam(next, 'endDate', event.target.value);
                    setSearchParams(next, { replace: true });
                  }}
                />
              </label>
            </div>
          ) : null}

          {showLoadingState ? <p>Loading zone trends...</p> : null}
          {trendsQuery.isError ? <p className="text-danger">{trendsQuery.error.message}</p> : null}

          {!showLoadingState && !trendsQuery.isError ? (
            zones.length > 0 ? (
              <ZoneTrendMatrix bonusTypes={bonusTypes} zones={zones} />
            ) : (
              <p className="text-body-secondary mb-0">No zones match the selected filters.</p>
            )
          ) : null}
        </section>
      </div>
    </main>
  );
}
